import { useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Form } from '@/components/ui/form';
import { CirclePlus } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import SpinnerButton from '@/components/common/SpinnerButton';
import Error from '@/components/common/Error';
import FormGeneral from './common/FormGeneral';
import { TIME_ZONES } from '../enums';

import { useRegions, usePostRegion } from '@/data/region';

const formSchema = z.object({
  name: z.string().min(1),
  timeZone: z.string().min(1)
});

const AddRegionDialog = () => {
  const [open, setIsOpen] = useState(false);
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const postRegion = usePostRegion();
  const regions = useRegions();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      timeZone: TIME_ZONES['America/Vancouver']
    }
  });

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      setApiError('');
      const region = await postRegion(values);
      await regions.mutate(
        (currentRegions) => [...currentRegions, region],
        false
      );
      setSubmitting(false);
      setIsOpen(false);
    } catch (err) {
      setApiError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>
          <CirclePlus />
          Add Region
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[600px]">
        <DialogHeader>
          <DialogTitle>Add Region</DialogTitle>
          <DialogDescription>
            Add a Region such as &quot;The Kootenays&quot;
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormGeneral />
            {apiError && <Error error={apiError} />}
            <DialogFooter>
              <SpinnerButton loading={submitting}>Add Region</SpinnerButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddRegionDialog;
